import { useEffect, useState } from 'react'
import { Modal, FormField, Alert } from './ui'
import { btnPrimary, btnSecondary, colors } from './ui/styles'
import { api, ApiError } from '../services/api'
import { useAuth } from '../hooks/use-auth'
import { useSupportEmail } from '../hooks/use-support-email'

interface Props {
  readonly isOpen: boolean
  readonly onClose: () => void
}

// Help/Support form (issue #207). Any authenticated user can submit a
// request. The canonical triage record + SES confirmation live on Central;
// on a customer/edge deployment the same POST is forwarded to Central over
// the fleet_reporting channel (issue #225). If forwarding isn't configured
// or Central is unreachable, the API returns 503 + a support email and we
// fall back to a direct mailto rather than silently dropping the request.
export function SupportRequestModal({ isOpen, onClose }: Props) {
  const { user } = useAuth()
  const supportEmail = useSupportEmail()

  const profileEmail = user?.email ?? ''
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [email, setEmail] = useState(profileEmail)
  const [phone, setPhone] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)
  // Set when the API responds 503 (no live/forwarded submission path is
  // available) so the modal falls back to a direct mailto.
  const [unavailable, setUnavailable] = useState(false)
  // Email-change → profile-update prompt state.
  const [offerProfileUpdate, setOfferProfileUpdate] = useState(false)
  const [profileUpdated, setProfileUpdated] = useState(false)
  const [profileUpdateError, setProfileUpdateError] = useState<string | null>(null)

  // Reset + prefill (including profile phone) each time the modal opens.
  useEffect(() => {
    if (!isOpen) return
    setSubject('')
    setMessage('')
    setEmail(profileEmail)
    setPhone('')
    setSubmitting(false)
    setError(null)
    setSubmitted(false)
    setUnavailable(false)
    setOfferProfileUpdate(false)
    setProfileUpdated(false)
    setProfileUpdateError(null)
    // Phone isn't in the auth context; pull the full profile to prefill it.
    api
      .get<{ phone?: string | null; email?: string }>('/users/me')
      .then((res) => {
        if (res.data?.phone) setPhone(res.data.phone)
        if (res.data?.email) setEmail((cur) => cur || res.data!.email!)
      })
      .catch(() => {
        /* prefill is best-effort */
      })
  }, [isOpen, profileEmail])

  const handleSubmit = async () => {
    setError(null)
    setSubmitting(true)
    try {
      await api.post('/support-requests', {
        subject: subject.trim(),
        message: message.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
      })
      setSubmitted(true)
      // If they changed the contact email, offer to update their profile.
      if (email.trim() && email.trim().toLowerCase() !== profileEmail.toLowerCase()) {
        setOfferProfileUpdate(true)
      }
    } catch (e) {
      // 503 = no live/forwarded submission path available (edge with no
      // fleet_reporting channel, or Central unreachable) -- fall back to a
      // direct mailto instead of surfacing it as a form error.
      if (e instanceof ApiError && e.status === 503) {
        setUnavailable(true)
      } else {
        setError(e instanceof ApiError ? e.message : 'Failed to submit support request')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleUpdateProfileEmail = async () => {
    setProfileUpdateError(null)
    try {
      await api.patch('/users/me/email', { email: email.trim() })
      setProfileUpdated(true)
      setOfferProfileUpdate(false)
    } catch (e) {
      setProfileUpdateError(e instanceof ApiError ? e.message : 'Failed to update profile email')
    }
  }

  const canSubmit = subject.trim().length > 0 && message.trim().length > 0 && email.trim().length > 0 && !submitting

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Help &amp; Support" width="520px">
      {unavailable ? (
        // No live/forwarded submission path (edge with no fleet_reporting
        // channel configured, or Central unreachable after the one retry).
        <div style={{ fontSize: '0.875rem', color: colors.text, lineHeight: 1.5 }}>
          <p>To reach MedFlow support from this deployment, please email us directly:</p>
          <p>
            <a href={`mailto:${supportEmail}`} style={{ color: colors.accent, fontWeight: 600 }}>
              {supportEmail}
            </a>
          </p>
        </div>
      ) : submitted ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <Alert
            type="success"
            message={`Support request received. A confirmation has been sent to ${email.trim()} (copied to ${supportEmail}).`}
          />

          {offerProfileUpdate && !profileUpdated && (
            <div
              style={{
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                padding: '0.75rem',
                fontSize: '0.875rem',
                color: colors.text,
              }}
            >
              You used a different email than your profile ({profileEmail}). Update your profile email to{' '}
              <strong>{email.trim()}</strong>?
              {profileUpdateError && (
                <div style={{ marginTop: '0.5rem' }}>
                  <Alert type="error" message={profileUpdateError} />
                </div>
              )}
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                <button type="button" style={btnPrimary} onClick={handleUpdateProfileEmail}>
                  Update profile email
                </button>
                <button type="button" style={btnSecondary} onClick={() => setOfferProfileUpdate(false)}>
                  No thanks
                </button>
              </div>
            </div>
          )}

          {profileUpdated && <Alert type="success" message="Your profile email has been updated." />}

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" style={btnPrimary} onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {error && <Alert type="error" message={error} />}
          <FormField name="support-subject" label="Subject" required value={subject} onChange={setSubject} />
          <FormField
            name="support-message"
            label="How can we help?"
            required
            multiline
            rows={5}
            value={message}
            onChange={setMessage}
          />
          <FormField
            name="support-email"
            label="Reply-to email"
            required
            type="email"
            value={email}
            onChange={setEmail}
          />
          <FormField name="support-phone" label="Phone (optional)" value={phone} onChange={setPhone} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
            <button type="button" style={btnSecondary} onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type="button"
              style={{ ...btnPrimary, opacity: canSubmit ? 1 : 0.6 }}
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {submitting ? 'Submitting…' : 'Submit request'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  )
}
